import React, { useEffect, useState } from "react";
import api from "../../api/axiosConfig";
import format from "date-fns/format";
import parse from "date-fns/parse";
import {
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
} from "react-icons/md";
import { useAuth } from "../Authentication/AuthContext";

const CalendarChart = ({ projectId }) => {
  const [calendarData, setCalendarData] = useState([]);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const { userDetails } = useAuth();
  const roleName = userDetails.role_name;
  const userId = userDetails.id;
  const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const getDaysInMonth = (month, year) =>
    new Date(year, month + 1, 0).getDate();

  const fetchCalendarData = async (month, year) => {
    try {
      const res = await api.get(`/api/timesheet/calendar/${projectId}`, {
        params: { month: month + 1, year },
      });
      const formattedData = res.data.map((entry) => {
        const dayDate = new Date(entry.day);
        const dayName = format(dayDate, "EEEE");
        return {
          ...entry,
          day: format(dayDate, "dd-MM-yyyy"),
          dayName,
        };
      });
      setCalendarData(formattedData);
    } catch (error) {
      console.error(
        "Error fetching calendar data:",
        error.response?.data || error.message
      );
      setError("Failed to load calendar data.");
    }
  };

  useEffect(() => {
    fetchCalendarData(currentMonth, currentYear);
  }, [currentMonth, currentYear, projectId]);

  const getTotalBookedHoursForDay = (day) => {
    const currentDate = new Date(currentYear, currentMonth, day);
    const totalSeconds = calendarData.reduce((acc, entry) => {
      const entryDate = parse(entry.day, "dd-MM-yyyy", new Date());
      if (entryDate.getTime() === currentDate.getTime()) {
        if (roleName !== "Administrator" && entry.user_id !== userId)
          return acc;
        const [hours, minutes] = entry.booked_hours.split(":").map(Number);
        return acc + hours * 3600 + minutes * 60;
      }
      return acc;
    }, 0);
    return convertSecondsToHM(totalSeconds);
  };

  const convertSecondsToHM = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}`;
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const firstDayIndex = new Date(currentYear, currentMonth, 1).getDay();
  const currentMonthDays = getDaysInMonth(currentMonth, currentYear);
  const prevMonthDays = getDaysInMonth(currentMonth - 1, currentYear);
  const nextMonthDays = 42 - (firstDayIndex + currentMonthDays);

  return (
    <div>
      <div className="calendar-controls">
        <button
          onClick={handlePrevMonth}
          style={{ border: "none", cursor: "pointer" }}
        >
          <MdKeyboardDoubleArrowLeft style={{ verticalAlign: "middle" }} />
        </button>
        <p>{`${monthNames[currentMonth]} ${currentYear}`}</p>
        <button
          onClick={handleNextMonth}
          style={{ border: "none", cursor: "pointer" }}
        >
          <MdKeyboardDoubleArrowRight style={{ verticalAlign: "middle" }} />
        </button>
      </div>
      <div className="calendar-grid">
        {dayNames.map((day) => (
          <div className="calendar-day-label" key={day}>
            {day}
          </div>
        ))}
        {Array.from({ length: firstDayIndex }).map((_, index) => (
          <div
            className="calendar-day calendar-prev-month"
            key={index}
            style={{ color: "#ccc", textAlign: "right" }}
          >
            {prevMonthDays - firstDayIndex + index + 1}
          </div>
        ))}
        {[...Array(currentMonthDays).keys()].map((day) => {
          const totalBookedHours = getTotalBookedHoursForDay(day + 1);
          const dayDate = new Date(currentYear, currentMonth, day + 1);
          const isToday = dayDate.toDateString() === new Date().toDateString();
          return (
            <div
              className={`calendar-day ${isToday ? "calendar-today" : " "}`}
              key={day}
              style={{
                backgroundColor: "transparent",
                fontWeight: "400",
                color:
                  dayDate.getDay() === 0 ||
                  (dayDate.getDay() === 6 && Math.ceil((day + 1) / 7) % 2 === 1)
                    ? "red"
                    : "black",
                // display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  width: isToday ? "30px" : "auto",
                  height: isToday ? "20px" : "auto",
                  borderRadius: isToday ? "50%" : "0",
                  backgroundColor: isToday ? "#7ecafc" : "transparent",

                  flexDirection: "column",
                  alignItems: "center",
                  padding: isToday ? "1px" : "0",
                  top: isToday ? "-1px" : "0",
                  right:
                    window.innerWidth >= 1440 && window.innerWidth <= 1919
                      ? isToday
                        ? "-2.5rem"
                        : "-1.7rem"
                      : isToday
                      ? "-4rem"
                      : "-2.5rem",
                }}
              >
                {day + 1}
              </div>
              <div
                style={{
                  color: "#6200ee",
                  display: totalBookedHours === "00:00" ? "none" : "block",
                  fontSize: "14px",
                  right:
                    window.innerWidth >= 1440 && window.innerWidth <= 1919
                      ? isToday
                        ? "1.3rem"
                        : "1.3rem"
                      : isToday
                      ? "2rem"
                      : "2rem",
                  top: isToday ? "0.5rem" : "0.7rem",
                }}
              >
                {totalBookedHours}
              </div>
            </div>
          );
        })}
        {Array.from({ length: nextMonthDays }).map((_, index) => (
          <div
            className="calendar-day calendar-next-month"
            key={index}
            style={{ color: "#ccc", textAlign: "right" }}
          >
            {index + 1}
          </div>
        ))}
      </div>
      {error && <div className="error-message">{error}</div>}
    </div>
  );
};

export default CalendarChart;
